import { path } from 'utils'
import { z } from 'zod'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { type StreamScopedPathParams, streamScopedPathSchema } from './shared'
/**
 * - major - The year in which the software was released
 * - minor - The category of release (see more below)
 * - patch - The number of patches released in the given year and category
 *
 * Categories of 2020 software release:
 * - 0: Production: this is stable, production-ready software
 * (in practice, usually last year's Limited Release)
 * - 1: Limited: the latest feature set made public, which sees
 * more patch releases than Production, so is less stable
 * - 10: Beta: this is released to customers who have signed NDAs
 * and are trialing new features
 * - 1xx: Alpha: for internal users only
 *
 * @group Domain Concepts
 */
export type SoftwareVersion = z.infer<typeof softwareVersionSchema>

export const softwareVersionSchema = z.object({
  major: z.number(),
  minor: z.number(),
  patch: z.number()
})

export const diagnosticsSchema = z.object({
  /**
   * The total number of records (with data) in this 2020 file.
   */
  record_count: z.number(),
  /**
   * The software version that the dbm was running at the time of this record.
   */
  software_version: softwareVersionSchema,
  /**
   * records where the configuration has changed; will always include 0;
   * sometimes the "same" config will appear twice if the dbm powered on and off again
   */
  config_changes: z.array(z.number())
})

type Params = StreamScopedPathParams & {
  record?: number
  lastRecordId?: number
}

const pathFor = (params: Params) => {
  const { orgCode, streamId, lastRecordId, record, requestType } = params
  return path(`${requestType}/streams/${orgCode}/${streamId}/diagnostics`, {
    lastRecordId,
    record
  })
}

export const diagnosticSummaryApi = defineApi({
  pathSchema: streamScopedPathSchema,
  querySchema: z.object({
    record: z.number().optional(),
    lastRecordId: z.number().optional()
  }),
  pathFor,
  name: 'GetDiagnosticsSummary',
  description:
    "Get a stream's diagnostic overview: record_count (how many data records the stream has), the 20|20 software_version, and config_changes — the exact record numbers at which the configuration changed (always includes 0, the first record; the same config can appear twice if the display was power-cycled). Use it before GetApplicationConfig or GetEquipmentConfig when you need config at a particular point in the stream: config_changes lists the records that have distinct configurations, and record_count bounds the valid record range so a record you pass actually exists. Requires a streamId.",
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema: diagnosticsSchema
})

/**
 * Top-level diagnostics data true of the whole stream
 *
 * @group Routes
 * @group Domain Concepts
 *
 * @route
 * `GET /{stitch_prefix}/streams/{orgCode}/{streamId}/diagnostics`
 *
 * - record_count - The number of records in the stream exposed by the API (may be less than the 'actual' total in the raw file)
 * - software_version - The version of the software running on the stream,
 * which is given in the form of an object with major, minor, and patch properties to allow filtering
 */
export type DiagnosticsOverview = z.infer<typeof diagnosticsSchema>
